import React, { useEffect, useState } from "react";
import { ExternalLink, LogOut, Images, ImageIcon, Lock, Heart } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { adminApi, errMsg } from "../lib/api";
import { useSite } from "../context/SiteContext";
import SiteImagesPanel from "./SiteImagesPanel";
import GalleryPanel from "./GalleryPanel";
import MembersPanel from "./MembersPanel";
import ContentPanel from "./ContentPanel";
import LibraryPanel from "./LibraryPanel";
import VideosPanel from "./VideosPanel";

export default function AdminDashboard({ user, onLogout }) {
  const { images, reload } = useSite();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadGallery = async () => {
    try {
      const { data } = await adminApi.get("/admin/gallery");
      setGallery(data);
    } catch (e) {
      if (e?.response?.status === 401) return onLogout();
      toast.error(errMsg(e, "Could not load gallery"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadGallery(); reload(); }, []); // eslint-disable-line

  const refresh = async () => { await Promise.all([loadGallery(), reload()]); };
  const lockedCount = gallery.filter((g) => g.locked).length;

  const stats = [
    { label: "Gallery photos", value: gallery.length, icon: Images },
    { label: "VIP-locked", value: lockedCount, icon: Lock },
    { label: "Public", value: gallery.length - lockedCount, icon: Heart },
    { label: "Site images", value: Object.keys(images || {}).length, icon: ImageIcon },
  ];

  return (
    <div>
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-[#f0e4e8]">
        <div className="max-w-[1180px] mx-auto px-6 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="logo-script !text-[34px] !text-shadow-none" style={{ textShadow: "none" }}>Silky Maya</span>
            <span className="admin-pill">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-[13px] text-[#7a7276] mr-2">Signed in as <b className="text-[#1b1719]">{user}</b></span>
            <a href="/" target="_blank" rel="noreferrer" className="admin-ghost" data-testid="admin-view-site"><ExternalLink className="w-4 h-4" /> View site</a>
            <button onClick={onLogout} className="admin-ghost" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1180px] mx-auto px-6 py-10">
        <h1 className="serif text-[38px] text-[#1b1719]">Dashboard</h1>
        <p className="text-[15px] text-[#7a7276] mt-1">Manage members, chats, member content and site images. Changes go live instantly.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {stats.map((s) => (
            <div key={s.label} className="admin-card p-5 flex items-center gap-4" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="w-11 h-11 rounded-xl bg-[#fff0f5] text-[#ee3a70] grid place-items-center"><s.icon className="w-5 h-5" /></span>
              <div><p className="text-[24px] font-semibold leading-none text-[#1b1719]">{s.value}</p><p className="text-[12.5px] text-[#7a7276] mt-1">{s.label}</p></div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="members" className="mt-10">
          <TabsList className="bg-[#f6eef1] h-auto p-1 flex-wrap">
            <TabsTrigger value="members" className="admin-tab" data-testid="tab-members">Members & chats</TabsTrigger>
            <TabsTrigger value="content" className="admin-tab" data-testid="tab-content">Member content</TabsTrigger>
            <TabsTrigger value="videos" className="admin-tab" data-testid="tab-videos">VIP videos</TabsTrigger>
            <TabsTrigger value="library" className="admin-tab" data-testid="tab-library">Member library</TabsTrigger>
            <TabsTrigger value="gallery" className="admin-tab" data-testid="tab-gallery">Public gallery</TabsTrigger>
            <TabsTrigger value="site" className="admin-tab" data-testid="tab-site">Site images</TabsTrigger>
          </TabsList>
          <TabsContent value="members" className="mt-6"><MembersPanel /></TabsContent>
          <TabsContent value="content" className="mt-6"><ContentPanel /></TabsContent>
          <TabsContent value="videos" className="mt-6"><VideosPanel /></TabsContent>
          <TabsContent value="library" className="mt-6"><LibraryPanel /></TabsContent>
          <TabsContent value="gallery" className="mt-6">
            <GalleryPanel gallery={gallery} setGallery={setGallery} loading={loading} refresh={refresh} />
          </TabsContent>
          <TabsContent value="site" className="mt-6">
            <SiteImagesPanel images={images} refresh={refresh} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
